/**
 * Segmented tabs with optional live counts (§18 queue status tabs). A real
 * tablist following the ARIA pattern: roving tabindex (only the active tab is in
 * the Tab order) and ←/→/Home/End move + activate. Presentational — the parent
 * owns the selected id + data.
 */
import { useRef } from "react";

export interface TabItem {
  id: string;
  label: string;
  /** Live count shown as a pill; omit/undefined hides it. */
  count?: number | null;
}

export function Tabs({
  items,
  value,
  onChange,
  ariaLabel,
}: {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  ariaLabel?: string;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIdx = items.findIndex((t) => t.id === value);

  const focusAndSelect = (i: number) => {
    const t = items[i];
    if (!t) return;
    onChange(t.id);
    refs.current[i]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const n = items.length;
    if (n === 0) return;
    const cur = selectedIdx < 0 ? 0 : selectedIdx;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      focusAndSelect((cur + 1) % n);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusAndSelect((cur - 1 + n) % n);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusAndSelect(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusAndSelect(n - 1);
    }
  };

  return (
    <div className="tabs" role="tablist" aria-label={ariaLabel} onKeyDown={onKeyDown}>
      {items.map((t, i) => {
        const active = value === t.id;
        // Roving tabindex: the active tab (or the first, if none active) is the
        // single Tab stop.
        const tabStop = active || (selectedIdx < 0 && i === 0);
        return (
          <button
            key={t.id || "_all"}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={tabStop ? 0 : -1}
            className={`tab${active ? " tab--active" : ""}`}
            onClick={() => onChange(t.id)}
          >
            <span className="tab__label">{t.label}</span>
            {t.count != null && <span className="tab__count tnum">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
